import classNames from 'classnames'
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'

export declare interface DropdownItemProps {
    title: string | React.ReactNode
    toggleFunction?: () => void
    active?: boolean
    nestedItems?: DropdownItemProps[] // So this can be a potentially cascading menu
}

export declare interface DropdownMenuProps {
    title: string
    buttons: DropdownItemProps[]
    open: boolean
    setOpen: Dispatch<SetStateAction<boolean>>
}

export function DropdownItem({ title, nestedItems, toggleFunction, active }: DropdownItemProps): JSX.Element {
    const [submenuOpen, setSubmenuOpen] = useState(false)

    // As a DropdownItem can have its open Dropdown Menu, we need to check if it has children to conditonally render the guiding arrows and children.
    const hasNestedItems = nestedItems && nestedItems.length > 0

    const liAdditionalClasses = hasNestedItems ? 'rounded-sm relative' : ''

    const listItemClasses = classNames('px-3 py-1 hover:bg-gray-200 cursor-pointer', 'bg-slate-100 dark:bg-slate-800', liAdditionalClasses)

    const buttonClasses = classNames('w-full text-left flex items-center outline-none focus:outline-none')

    const unorderedListClasses = classNames(
        'bg-white border rounded-sm absolute top-0 right-0',
        `tranform scale-0 ${submenuOpen && 'scale-100'} transition duration-300 ease-in-out`,
        'translate-x-full origin-top-left min-w-max'
    )

    const handleMouseOver = (e: React.MouseEvent) => {
        setSubmenuOpen(true)
    }

    const handleMouseLeave = (e: React.MouseEvent) => {
        setSubmenuOpen(false)
    }

    // Handle when an option, such as Heading 1, is clicked (it should toggle the rich text setting)
    // NOTE: Must add listener for onMouseDown and not onClick due to known Draft JS Editor focus issues
    const handleOnMouseDownOption = (e: React.MouseEvent) => {
        e.preventDefault() // Must call preventDefault() to prevent Draft JS issue

        if (!toggleFunction) return

        toggleFunction()
    }

    return (
        <li className={listItemClasses} onMouseOver={handleMouseOver} onMouseLeave={handleMouseLeave} onMouseDown={handleOnMouseDownOption}>
            {hasNestedItems ? (
                // Did not use <Button></Button> component because this button are styled very particularly for the RichTextDropdown component
                <button type="button" className={buttonClasses}>
                    <span className="pr-1 flex-1">{title}</span>
                    <span className="mr-auto">
                        <svg
                            className={`fill-current h-4 w-4 transition -rotate-90 ${submenuOpen && 'rotate-90'} duration-300 ease-in-out`}
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                        >
                            <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                        </svg>
                    </span>
                </button>
            ) : (
                <span className="pr-1 flex-1">
                    {title}
                    <svg
                        className={`h-5 w-5 inline ml-1 tranform scale-0 ${active && 'scale-100'} transition duration-100 ease-in-out fill-slate-800 dark:fill-slate-100`}
                        viewBox="0 0 20 20"
                    >
                        <path d="M18.2 5.4a1 1 0 011.6 1.2l-8 12a1 1 0 01-1.5.1l-5-5a1 1 0 111.4-1.4l4.1 4.1 7.4-11z" />
                    </svg>
                </span>
            )}
            {hasNestedItems && (
                <ul className={unorderedListClasses}>
                    {nestedItems.map((item) => {
                        return <DropdownItem title={item.title} nestedItems={item.nestedItems} toggleFunction={item.toggleFunction} active={item.active} />
                    })}
                </ul>
            )}
        </li>
    )
}

export function Dropdown({ title, buttons, open, setOpen }: DropdownMenuProps): JSX.Element {
    const ref: React.MutableRefObject<HTMLDivElement | null> = useRef(null) // This ref will be used to handle closing the dropdown when the user clicks outside of it.

    const buttonClasses = classNames(
        'outline-none focus:outline-none border px-3 py-1 bg-white rounded-sm flex items-center min-w-32',
        'bg-slate-100 dark:bg-slate-800'
    )

    const unorderedListClasses = classNames('bg-white border rounded-sm absolute origin-top min-w-max')

    useEffect(() => {
        const handleOutsideClick = (e: MouseEvent) => {
            if (e.target === null) return

            if (ref.current && !ref.current.contains(e.target as Node)) {
                setOpen(false)
            }
        }

        document.addEventListener('mousedown', handleOutsideClick)

        return () => {
            document.removeEventListener('mousedown', handleOutsideClick)
        }
    }, [])

    // Clicking the main button to toggle the dropdown
    const handleDropdownBtnClick = (e: React.MouseEvent) => {
        e.preventDefault() // Must call preventDefault() to prevent Draft JS issue
        setOpen(!open)
    }

    return (
        // Did not use <Button></Button> component because this button are styled very particularly for the RichTextDropdown component
        <div className="inline-block mr-1" ref={ref}>
            <button type="button" className={buttonClasses} onMouseDown={handleDropdownBtnClick}>
                <span className="pr-1 font-semibold flex-1">{title}</span>
                <span>
                    <svg
                        className={`fill-current h-4 w-4 transform transition ${open && '-rotate-180'} duration-300 ease-in-out`}
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                    >
                        <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                </span>
            </button>
            {open && (
                <ul className={unorderedListClasses} id="rich-text-dropdown-menu">
                    {buttons.map((b) => {
                        return <DropdownItem title={b.title} nestedItems={b.nestedItems} toggleFunction={b.toggleFunction} active={b.active} />
                    })}
                </ul>
            )}
        </div>
    )
}
